// src/pwa/src/services/pdfService.ts
// Issue 74 — Finance PDF Workflow + Master_Products Catalog Dropdown.
// Browser-native PDF generation for Quotes, Invoices, Budgets and Reports.
// Uses jspdf + jspdf-autotable; no Cloudflare Worker required, works offline.

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface QuotePdfData {
    id: string;
    quoteNumber?: string;
    clientId?: string;
    clientName?: string;
    items: Array<{
        description: string;
        quantity: number;
        rate: number;
        total: number;
        productCode?: string;
    }>;
    subtotal: number;
    tax?: number;
    totalTaxes?: number;
    total: number;
    status?: string;
    createdAt?: string;
    dueDate?: string;
    taxRate?: number;
    terms?: string;
    banking?: string;
}

export interface InvoicePdfData {
    id: string;
    invoiceNumber?: string;
    quoteId?: string;
    clientId?: string;
    clientName?: string;
    amount: number;
    subtotal?: number;
    balance?: number;
    paidToDate?: number;
    items: Array<{
        description: string;
        quantity: number;
        rate: number;
        total: number;
        productCode?: string;
    }>;
    totalTaxes?: number;
    taxRate?: number;
    status?: string;
    dueDate?: string;
    issuedAt?: string;
    terms?: string;
    banking?: string;
}

export interface BudgetPdfRow {
    id: string;
    category: string;
    period: string;
    budgeted: number;
    actual: number;
    variance: number;
    status: string;
}

export interface ReportPdfData {
    type: 'PnL' | 'BalanceSheet' | 'CashFlow';
    period: string;
    data: Record<string, number>;
    lineItems?: Array<{ label: string; amount: number }>;
    insights?: string[];
}

// ─── Brand chrome ────────────────────────────────────────────────────────────

const BRAND_BLUE: [number, number, number] = [37, 99, 235];
const SLATE_700: [number, number, number] = [51, 65, 85];
const SLATE_500: [number, number, number] = [100, 116, 139];

function header(doc: jsPDF, title: string, subtitle?: string): number {
    doc.setFillColor(...BRAND_BLUE);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 18, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(title, 14, 12);
    if (subtitle) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(subtitle, doc.internal.pageSize.getWidth() - 14, 12, { align: 'right' });
    }
    doc.setTextColor(...SLATE_700);
    return 26; // y-start for the body
}

function footer(doc: jsPDF) {
    const page = doc.getNumberOfPages();
    for (let i = 1; i <= page; i++) {
        doc.setPage(i);
        const w = doc.internal.pageSize.getWidth();
        const h = doc.internal.pageSize.getHeight();
        doc.setFontSize(8);
        doc.setTextColor(...SLATE_500);
        doc.text(`BizBuilder Platform  •  Generated ${new Date().toISOString().split('T')[0]}`, 14, h - 8);
        doc.text(`Page ${i} of ${page}`, w - 14, h - 8, { align: 'right' });
    }
}

function money(n: number): string {
    if (!Number.isFinite(n)) {return 'R 0.00';}
    return `R ${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Quote ──────────────────────────────────────────────────────────────────

export async function generateQuotePdf(quote: QuotePdfData): Promise<Blob> {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    let y = header(
        doc,
        `QUOTATION  ${quote.quoteNumber || quote.id}`,
        `Status: ${(quote.status || 'draft').toUpperCase()}`
    );

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Client', 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(quote.clientName || quote.clientId || '—'), 14, y + 5);

    doc.setFont('helvetica', 'bold');
    doc.text('Issued', 110, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(quote.createdAt || new Date().toISOString().split('T')[0]), 110, y + 5);

    if (quote.dueDate) {
        doc.setFont('helvetica', 'bold');
        doc.text('Valid Until', 150, y);
        doc.setFont('helvetica', 'normal');
        doc.text(String(quote.dueDate), 150, y + 5);
    }
    y += 14;

    autoTable(doc, {
        startY: y,
        head: [['#', 'Code', 'Description', 'Qty', 'Rate (R)', 'Total (R)']],
        body: (quote.items || []).map((it, i) => [
            i + 1,
            it.productCode || '',
            it.description || '',
            String(it.quantity),
            Number(it.rate).toFixed(2),
            Number(it.total).toFixed(2),
        ]),
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: BRAND_BLUE, textColor: [255, 255, 255] },
        columnStyles: {
            0: { halign: 'right', cellWidth: 8 },
            1: { cellWidth: 20 },
            3: { halign: 'right', cellWidth: 12 },
            4: { halign: 'right', cellWidth: 25 },
            5: { halign: 'right', cellWidth: 30 },
        },
    });

    y = (doc as any).lastAutoTable.finalY + 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Subtotal:', 142, y);
    doc.setFont('helvetica', 'normal');
    doc.text(money(Number(quote.subtotal || 0)), 196, y, { align: 'right' });

    if (quote.taxRate || quote.totalTaxes) {
        y += 6;
        doc.setFont('helvetica', 'bold');
        doc.text(`Tax (${quote.taxRate ?? 15}%):`, 142, y);
        doc.setFont('helvetica', 'normal');
        doc.text(money(Number(quote.totalTaxes ?? quote.tax ?? 0)), 196, y, { align: 'right' });
    }

    y += 8;
    doc.setDrawColor(...BRAND_BLUE);
    doc.setLineWidth(0.4);
    doc.line(120, y - 2, 196, y - 2);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Total:', 142, y + 4);
    doc.setTextColor(...BRAND_BLUE);
    doc.text(money(Number(quote.total || 0)), 196, y + 4, { align: 'right' });
    doc.setTextColor(...SLATE_700);

    y += 14;
    if (quote.terms) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Terms & Conditions', 14, y);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(String(quote.terms), 180);
        doc.text(lines, 14, y + 5);
        y += 5 + lines.length * 4;
    }
    if (quote.banking) {
        doc.setFont('helvetica', 'bold');
        doc.text('Banking Details', 14, y + 4);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(String(quote.banking), 180);
        doc.text(lines, 14, y + 9);
    }

    footer(doc);
    return doc.output('blob');
}

// ─── Invoice ────────────────────────────────────────────────────────────────

export async function generateInvoicePdf(invoice: InvoicePdfData): Promise<Blob> {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    let y = header(
        doc,
        `TAX INVOICE  ${invoice.invoiceNumber || invoice.id}`,
        `Status: ${(invoice.status || 'unpaid').toUpperCase()}`
    );

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To', 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(invoice.clientName || invoice.clientId || '—'), 14, y + 5);

    doc.setFont('helvetica', 'bold');
    doc.text('Issued', 110, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(invoice.issuedAt || '—'), 110, y + 5);

    doc.setFont('helvetica', 'bold');
    doc.text('Due', 150, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(invoice.dueDate || '—'), 150, y + 5);
    y += 14;

    autoTable(doc, {
        startY: y,
        head: [['#', 'Code', 'Description', 'Qty', 'Rate (R)', 'Total (R)']],
        body: (invoice.items || []).map((it, i) => [
            i + 1,
            it.productCode || '',
            it.description || '',
            String(it.quantity),
            Number(it.rate).toFixed(2),
            Number(it.total).toFixed(2),
        ]),
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: BRAND_BLUE, textColor: [255, 255, 255] },
        columnStyles: {
            0: { halign: 'right', cellWidth: 8 },
            1: { cellWidth: 20 },
            3: { halign: 'right', cellWidth: 12 },
            4: { halign: 'right', cellWidth: 25 },
            5: { halign: 'right', cellWidth: 30 },
        },
    });

    y = (doc as any).lastAutoTable.finalY + 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Subtotal:', 142, y);
    doc.setFont('helvetica', 'normal');
    doc.text(money(Number(invoice.subtotal || 0)), 196, y, { align: 'right' });

    if (invoice.taxRate || invoice.totalTaxes) {
        y += 6;
        doc.setFont('helvetica', 'bold');
        doc.text(`Tax (${invoice.taxRate ?? 15}%):`, 142, y);
        doc.setFont('helvetica', 'normal');
        doc.text(money(Number(invoice.totalTaxes || 0)), 196, y, { align: 'right' });
    }

    y += 8;
    doc.setDrawColor(...BRAND_BLUE);
    doc.line(120, y - 2, 196, y - 2);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Total:', 142, y + 4);
    doc.setTextColor(...BRAND_BLUE);
    doc.text(money(Number(invoice.amount || 0)), 196, y + 4, { align: 'right' });
    doc.setTextColor(...SLATE_700);

    y += 14;
    if (Number(invoice.paidToDate) > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Paid To Date:', 142, y);
        doc.setFont('helvetica', 'normal');
        doc.text(money(Number(invoice.paidToDate || 0)), 196, y, { align: 'right' });
        y += 6;
        doc.setFont('helvetica', 'bold');
        doc.text('Balance:', 142, y);
        doc.setFont('helvetica', 'normal');
        doc.text(money(Number(invoice.balance ?? invoice.amount ?? 0) - Number(invoice.paidToDate || 0)), 196, y, { align: 'right' });
        y += 6;
    }

    if (invoice.terms) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Terms & Conditions', 14, y);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(String(invoice.terms), 180);
        doc.text(lines, 14, y + 5);
        y += 5 + lines.length * 4;
    }
    if (invoice.banking) {
        doc.setFont('helvetica', 'bold');
        doc.text('Banking Details', 14, y + 4);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(String(invoice.banking), 180);
        doc.text(lines, 14, y + 9);
    }

    footer(doc);
    return doc.output('blob');
}

// ─── Budget ─────────────────────────────────────────────────────────────────

export async function generateBudgetPdf(
    rows: BudgetPdfRow[],
    periodLabel: string,
    totalBudgeted: number,
    totalActual: number,
): Promise<Blob> {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    let y = header(doc, 'BUDGET PLAN', `Period: ${periodLabel}`);

    autoTable(doc, {
        startY: y,
        head: [['Category', 'Period', 'Budgeted (R)', 'Actual (R)', 'Variance (R)', 'Status']],
        body: rows.map(r => [
            r.category,
            r.period,
            Number(r.budgeted).toFixed(2),
            Number(r.actual).toFixed(2),
            Number(r.variance).toFixed(2),
            (r.status || '').toUpperCase(),
        ]),
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: BRAND_BLUE, textColor: [255, 255, 255] },
        columnStyles: {
            2: { halign: 'right' },
            3: { halign: 'right' },
            4: { halign: 'right' },
        },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Totals', 142, y);
    doc.text(`Budgeted: ${money(totalBudgeted)}`, 142, y + 6);
    doc.text(`Actual:   ${money(totalActual)}`, 142, y + 12);
    doc.text(`Variance: ${money(totalBudgeted - totalActual)}`, 142, y + 18);

    footer(doc);
    return doc.output('blob');
}

// ─── Report ─────────────────────────────────────────────────────────────────

const REPORT_NAMES: Record<ReportPdfData['type'], string> = {
    PnL: 'Profit & Loss Report',
    BalanceSheet: 'Balance Sheet Report',
    CashFlow: 'Cash Flow Report',
};

export async function generateReportPdf(report: ReportPdfData): Promise<Blob> {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    let y = header(doc, REPORT_NAMES[report.type] || report.type, `Period: ${report.period}`);

    autoTable(doc, {
        startY: y,
        head: [['Metric', 'Amount (R)']],
        body: Object.entries(report.data || {}).map(([k, v]) => [
            k.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim(),
            Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        ]),
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: { fillColor: BRAND_BLUE, textColor: [255, 255, 255] },
        columnStyles: { 1: { halign: 'right' } },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
    if (report.lineItems && report.lineItems.length > 0) {
        autoTable(doc, {
            startY: y,
            head: [['Itemized Breakdown', 'Amount (R)']],
            body: report.lineItems.map(i => [
                i.label,
                Number(i.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            ]),
            styles: { fontSize: 9, cellPadding: 2 },
            headStyles: { fillColor: BRAND_BLUE, textColor: [255, 255, 255] },
            columnStyles: { 1: { halign: 'right' } },
        });
        y = (doc as any).lastAutoTable.finalY + 8;
    }

    if (report.insights && report.insights.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text('Insights', 14, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const lines = doc.splitTextToSize(report.insights.map(s => `• ${s}`).join('\n'), 180);
        doc.text(lines, 14, y + 5);
    }

    footer(doc);
    return doc.output('blob');
}

// ─── Download ───────────────────────────────────────────────────────────────

export function downloadPdf(blob: Blob, filename: string): string {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return filename;
}

// Utility used by the UI buttons.
export async function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
